import React, { useState } from "react";
import { Drawer, Select, Form, Checkbox, DatePicker, Icon, Upload, Tooltip } from "antd";
import moment from "moment";

import { useAxios } from "../../../../shared/hooks/useAxios";
import { useNotification } from "../../../../shared/hooks/useNotification";

const { Option } = Select;
const Dragger = Upload.Dragger;

const DetalheFaltaJustificadaDrawer = ({ inscricaoId, faltaJustificadaId, visible, onClose, atualizarFaltasJustificadas, form }) => {
    const [faltaJustificada, setFaltaJustificada] = useState({ motivo: undefined, dataInicio: null, dataTermino: null, variosDias: false });
    const [anexo, setAnexo] = useState([]);

    const { loading, notAuthorized, fetchData } = useAxios();
    const notify = useNotification();

    const FORMATOS_VALIDOS = ["zip", "rar", "7z", "pdf", "png", "jpeg", "jpg"];
    const TAMANHO_MAXIMO = 25 * 1024 * 1024; // 25 MB

    const handleChangeCheckbox = () => {
        form.resetFields(["dataTermino"]);
        setFaltaJustificada({ ...faltaJustificada, variosDias: !faltaJustificada.variosDias, dataTermino: null });
    };

    const handleSubmit = e => {
        e.preventDefault();

        form.validateFields((err, values) => {
            if (!err) {
                if (faltaJustificadaId && faltaJustificadaId > 0) {
                    alterarFaltaJustificada(values);
                } else {
                    adicionarFaltaJustificada(values);
                }
            }
        });
    };

    const carregarFaltaJustificada = async faltaJustificadaId => {
        const { data, error } = await fetchData({
            method: "get",
            url: `/api/faltas-justificadas/${faltaJustificadaId}`
        });

        if (!error) {
            setFaltaJustificada({
                motivo: data.motivo,
                dataInicio: moment(data.dataInicio),
                dataTermino: data.dataTermino ? moment(data.dataTermino) : null,
                variosDias: data.dataTermino ? true : false
            });
            setAnexo(
                data.anexoKey
                    ? [
                          {
                              uid: "-1",
                              name: data.anexoNomeOriginal,
                              status: "done",
                              url: data.anexoKey
                          }
                      ]
                    : []
            );
        } else {
            notify.error("Erro", error || "Ocorreu um erro inesperado. Por favor, tente novamente.");
        }
    };

    const adicionarFaltaJustificada = async values => {
        const formData = new FormData();
        formData.append("inscricaoId", inscricaoId);
        formData.append("motivo", values.motivo);
        formData.append("dataInicio", values.dataInicio.format("YYYY-MM-DD"));
        formData.append("dataTermino", values.dataTermino?.format("YYYY-MM-DD") ?? "");

        if (anexo[0]) {
            formData.append("anexo", anexo[0]);
        }

        const { error } = await fetchData({
            method: "post",
            url: "/api/faltas-justificadas",
            data: formData
        });

        if (!error) {
            notify.success("Sucesso", "Falta justificada adicionada!");
            atualizarFaltasJustificadas();
            onClose();
        } else {
            notify.error("Erro", error || "Ocorreu um erro inesperado. Por favor, tente novamente.");
        }
    };

    const alterarFaltaJustificada = async values => {
        const formData = new FormData();
        formData.append("id", faltaJustificadaId);
        formData.append("motivo", values.motivo);
        formData.append("dataInicio", values.dataInicio.format("YYYY-MM-DD"));
        formData.append("dataTermino", values.dataTermino?.format("YYYY-MM-DD") ?? "");

        if (anexo[0]) {
            if (anexo[0].status !== "done") {
                formData.append("anexo", anexo[0]);
            } else {
                formData.append("anexoKeyExistente", anexo[0].url);
            }
        }

        const { error } = await fetchData({
            method: "put",
            url: "/api/faltas-justificadas",
            data: formData
        });

        if (!error) {
            notify.success("Sucesso", "Falta justificada alterada!");
            atualizarFaltasJustificadas();
            onClose();
        } else {
            notify.error("Erro", error || "Ocorreu um erro inesperado. Por favor, tente novamente.");
        }
    };

    const validarDataInicio = (rule, value, callback) => {
        const dataTermino = form.getFieldValue("dataTermino");
        if (value && dataTermino && value.isAfter(dataTermino)) {
            callback("A data de início não pode ser maior que a data de fim");
        } else {
            callback();
        }
    };

    const validarDataTermino = (rule, value, callback) => {
        const dataInicio = form.getFieldValue("dataInicio");
        if (value && dataInicio && value.isBefore(dataInicio)) {
            callback("A data de fim não pode ser menor que a data de início");
        } else {
            callback();
        }
    };

    const afterVisibleChange = visible => {
        if (visible && faltaJustificadaId && faltaJustificadaId > 0) {
            carregarFaltaJustificada(faltaJustificadaId);
        }

        if (!visible) {
            form.resetFields();
            setFaltaJustificada({ motivo: undefined, dataInicio: null, dataTermino: null, variosDias: false, anexo: [] });
        }
    };

    const validar = (rule, value, callback) => {
        if (value.file) {
            const extensao = value.file.name.split(".").pop().toLowerCase();

            if (!validarFormatos(extensao, FORMATOS_VALIDOS) || value.file.size > TAMANHO_MAXIMO) callback("Campo obrigatório");
        }

        callback();
    };

    const validarFormatos = (formato, formatos_aceites) => {
        var valido = false;

        for (var i = 0; i < formatos_aceites.length; i++) {
            if (formato === formatos_aceites[i]) valido = true;
        }

        return valido;
    };

    const { getFieldDecorator } = form;

    const propsFileUpload = {
        accept: FORMATOS_VALIDOS.map(f => `.${f}`).join(", "),
        multiple: false,
        fileList: anexo,
        ficheiro: anexo,
        onRemove: () => {
            form.resetFields("download");
            setAnexo([]);
        },
        beforeUpload: file => {
            const extensao = file.name.split(".").pop().toLowerCase();

            if (!validarFormatos(extensao, FORMATOS_VALIDOS)) {
                notify.error("Ficheiro com formato inválido");
                return false;
            }

            if (file.size > TAMANHO_MAXIMO) {
                notify.error("Limite de 25 MB por upload");
                return false;
            }

            setAnexo([file]);
            return false;
        }
    };

    return (
        <Drawer
            className="drawer-add-cursos drawer-faltas-justificadas"
            width={720}
            onClose={onClose}
            closable={false}
            visible={visible}
            style={{
                overflow: "auto",
                height: "calc(100% - 108px)",
                paddingBottom: "108px"
            }}
            maskClosable={false}
            afterVisibleChange={afterVisibleChange}
        >
            <div className="bloco-info">
                <Form className="form-categorias" layout="horizontal" onSubmit={handleSubmit}>
                    <div className="bloco">
                        <Form.Item label="Motivo">
                            {getFieldDecorator("motivo", {
                                initialValue: faltaJustificada.motivo
                            })(
                                <Select placeholder="Selecionar" allowClear={true}>
                                    <Option value="Falta de transporte">Falta de transporte</Option>
                                    <Option value="Atestado médico">Atestado médico</Option>
                                    <Option value="Licença">Licença</Option>
                                    <Option value="Outros fatores">Outros fatores</Option>
                                </Select>
                            )}
                        </Form.Item>
                        <Form.Item label="Inicio">
                            {getFieldDecorator("dataInicio", {
                                initialValue: faltaJustificada.dataInicio,
                                rules: [
                                    {
                                        required: true,
                                        message: "Campo obrigatório"
                                    },
                                    {
                                        validator: validarDataInicio
                                    }
                                ]
                            })(<DatePicker className="input-25" placeholder="Selecionar" format="DD-MM-YYYY" name="dataInicio" />)}
                        </Form.Item>
                        <Form.Item>
                            {getFieldDecorator("variosDias", {
                                initialValue: faltaJustificada.variosDias,
                                valuePropName: "checked"
                            })(<Checkbox onChange={handleChangeCheckbox}>Justificação para vários dias</Checkbox>)}
                        </Form.Item>
                        {faltaJustificada.variosDias && (
                            <Form.Item label="Fim">
                                {getFieldDecorator("dataTermino", {
                                    initialValue: faltaJustificada.dataTermino,
                                    rules: [
                                        {
                                            required: true,
                                            message: "Campo obrigatório"
                                        },
                                        {
                                            validator: validarDataTermino
                                        }
                                    ]
                                })(<DatePicker className="input-25" placeholder="Selecionar" format="DD-MM-YYYY" name="dataTermino" />)}
                            </Form.Item>
                        )}
                        <Form.Item label="Anexo">
                            {getFieldDecorator("download", {
                                rules: [
                                    {
                                        validator: validar
                                    }
                                ],
                                initialValue: anexo
                            })(
                                <Dragger {...propsFileUpload}>
                                    <p className="ant-upload-drag-icon">
                                        <i className="fas fa-upload" />
                                    </p>
                                    <p className="ant-upload-text">
                                        Adicionar
                                        <Tooltip title="Formatos válidos: .zip, .rar, .7z, .pdf, .png, .jpeg, .jpg">
                                            <Icon type="question-circle-o" />
                                        </Tooltip>
                                    </p>
                                    <p className="ant-upload-hint">Limite de 25MB por upload</p>
                                </Dragger>
                            )}
                        </Form.Item>
                    </div>
                    <div className="ant-drawer-footer">
                        <button
                            className="botao-secundario"
                            type="button"
                            onClick={onClose}
                            style={{ marginRight: 20, display: "inline-block" }}
                        >
                            Voltar
                        </button>
                        <button className="botao-principal" type="submit" htmlType="submit" disabled={loading}>
                            {loading && <Icon type="loading" />}
                            Salvar
                        </button>
                    </div>
                </Form>
            </div>
        </Drawer>
    );
};

const FormDetalheFaltaJustificadaDrawer = Form.create({ name: "form-drawer-detalhe" })(DetalheFaltaJustificadaDrawer);

export default FormDetalheFaltaJustificadaDrawer;
